import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/ui/Header';
import Footer from './you-tube-video-downloader-landing-page/components/Footer';
import SplashCursor from './you-tube-video-downloader-landing-page/components/SplashCursor';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Icon from '../components/AppIcon';

const DownloadPage = () => {
  const [url, setUrl] = useState('');
  const [videoInfo, setVideoInfo] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, fetching_info, downloading, completed, error
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [downloadCompletedUrl, setDownloadCompletedUrl] = useState(null);
  const [recentDownloads, setRecentDownloads] = useState([]);

  const pollingIntervalRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    try {
      const savedDownloads = localStorage.getItem('recentDownloads');
      if (savedDownloads) {
        setRecentDownloads(JSON.parse(savedDownloads));
      }
    } catch (error) {
      console.error("Failed to load recent downloads from local storage:", error);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('recentDownloads', JSON.stringify(recentDownloads));
    } catch (error) {
      console.error("Failed to save recent downloads to local storage:", error);
    }
  }, [recentDownloads]);

  const fetchVideoInfo = async (videoUrl) => {
    setStatus('fetching_info');
    setErrorMessage('');
    setVideoInfo(null);
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    setDownloadCompletedUrl(null);
    setDownloadProgress(0);
    setSessionId(null);

    try {
      const response = await fetch('https://zeedownloader-api.onrender.com/fetch_info', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: videoUrl }),
        signal: AbortSignal.timeout(60000),
      });
      const data = await response.json();
      if (response.ok) {
        setVideoInfo(data);
        setStatus('idle');
      } else {
        console.error('Fetch info failed:', response.status, data);
        setErrorMessage(data.error || 'Failed to fetch video info.');
        setStatus('error');
      }
    } catch (error) {
      console.error('Fetch info error:', error.name, error.message, error.stack);
      setErrorMessage(`Failed to fetch video info: ${error.message}`);
      setStatus('error');
    }
  };

  const handleDownload = async (downloadType) => {
    if (!url?.trim()) {
      alert('Please enter a valid YouTube URL');
      return;
    }

    setStatus('downloading');
    setDownloadProgress(0);
    setErrorMessage('Starting download, server may take up to 30s to wake up...');
    setDownloadCompletedUrl(null);

    try {
      const response = await fetch('https://zeedownloader-api.onrender.com/download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, type: downloadType }),
        signal: AbortSignal.timeout(60000),
      });

      const data = await response.json();
      if (response.ok) {
        setSessionId(data.session_id);
        startPolling(data.session_id, downloadType);
        setErrorMessage('');
      } else {
        console.error('Download request failed:', response.status, data);
        setErrorMessage(data.error || 'Failed to start download.');
        setStatus('error');
      }
    } catch (error) {
      console.error('Download fetch error:', error.name, error.message, error.stack);
      setErrorMessage(`Failed to start download: ${error.message}`);
      setStatus('error');
    }
  };

  const startPolling = (currentSessionId, downloadType) => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await fetch(`https://zeedownloader-api.onrender.com/progress/${currentSessionId}`, {
          signal: AbortSignal.timeout(60000),
        });
        const data = await response.json();

        if (data.status === 'error') {
          clearInterval(pollingIntervalRef.current);
          setErrorMessage('Error: ' + (data.error || 'Download failed.'));
          setStatus('error');
        } else if (data.status === 'completed') {
          clearInterval(pollingIntervalRef.current);
          setDownloadProgress(100);
          setStatus('completed');
          setDownloadCompletedUrl(`https://zeedownloader-api.onrender.com/download_file/${currentSessionId}`);

          if (videoInfo) {
            const newDownload = {
              title: videoInfo.title,
              url: url,
              type: downloadType,
              date: new Date().toISOString(),
              sessionId: currentSessionId,
            };
            setRecentDownloads((prevDownloads) => [newDownload, ...prevDownloads.slice(0, 4)]);
          }
        } else {
          setDownloadProgress(data.progress);
        }
      } catch (error) {
        console.error('Progress fetch error:', error.name, error.message, error.stack);
        clearInterval(pollingIntervalRef.current);
        setErrorMessage(`Failed to track progress: ${error.message}`);
        setStatus('error');
      }
    }, 1000);
  };

  const handleClientDownload = () => {
    if (downloadCompletedUrl) {
      window.open(downloadCompletedUrl, '_blank');
    }
  };

  const handleReDownload = async (downloadUrl, downloadType) => {
    setVideoInfo(null);
    await fetchVideoInfo(downloadUrl);
    handleDownload(downloadType);
  };

  const isDownloading = status === 'downloading';

  useEffect(() => {
    if (url.startsWith('http')) {
      fetchVideoInfo(url);
    } else {
      setVideoInfo(null);
      setErrorMessage('');
      setStatus('idle');
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    }
  }, [url]);

  return (
    <>
      <Helmet>
        <title>Download YouTube Videos - Zee Downloader</title>
        <meta name="description" content="Download your favorite YouTube videos in high quality. Fast, secure, and easy-to-use video downloader." />
      </Helmet>
      <div className="min-h-screen bg-background relative overflow-hidden">
        <SplashCursor />

        <div className="fixed inset-0 bg-background/20 backdrop-blur-sm" style={{ zIndex: 2 }} />

        <div className="relative" style={{ zIndex: 50 }}>
          <Header />
        </div>

        <main className="relative pt-20 pb-8" style={{ zIndex: 10 }}>
          <div className="glass-container">
            <div className="text-center py-12 mb-8">
              <div className="glass-card p-8 max-w-3xl mx-auto">
                <h1 className="text-fluid-4xl font-bold text-foreground mb-4">
                  Download YouTube Videos
                </h1>
                <p className="text-fluid-lg text-muted-foreground">
                  Enter the YouTube video URL and choose your preferred quality to start downloading.
                </p>
              </div>
            </div>

            <div className="max-w-4xl mx-auto mb-12">
              <div className="glass-card p-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      YouTube Video URL
                    </label>
                    <Input
                      type="url"
                      placeholder="https://www.youtube.com/watch?v=..."
                      value={url}
                      onChange={(e) => setUrl(e?.target?.value)}
                      className="w-full"
                      disabled={isDownloading}
                    />
                  </div>

                  {(status === 'fetching_info' || errorMessage || videoInfo) && (
                    <div className="glass-panel p-4">
                      {status === 'fetching_info' && (
                        <div className="text-sm font-medium text-foreground">
                          Fetching video info, server may take up to 30s to wake up...
                        </div>
                      )}
                      {errorMessage && (
                        <div className="text-sm text-red-500 font-medium">
                          {errorMessage}
                        </div>
                      )}
                      {videoInfo && (
                        <div className="flex items-center space-x-4">
                          <img
                            src={videoInfo.thumbnail}
                            alt="Video Thumbnail"
                            className="w-24 h-24 rounded-lg object-cover"
                          />
                          <div className="flex-1">
                            <h3 className="font-medium text-foreground">
                              {videoInfo.title}
                            </h3>
                            <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                              <span>Ready to download</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {(videoInfo && status !== 'completed') && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Download Type
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <button
                          onClick={() => handleDownload('video')}
                          disabled={isDownloading}
                          className={`glass-panel p-4 text-left smooth-transition micro-lift ${
                            isDownloading ? 'opacity-50 cursor-not-allowed' : 'hover:glass-medium'
                          }`}
                        >
                          <div className="font-medium text-foreground">
                            Video (MP4)
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Highest quality available
                          </div>
                        </button>
                        <button
                          onClick={() => handleDownload('audio')}
                          disabled={isDownloading}
                          className={`glass-panel p-4 text-left smooth-transition micro-lift ${
                            isDownloading ? 'opacity-50 cursor-not-allowed' : 'hover:glass-medium'
                          }`}
                        >
                          <div className="font-medium text-foreground">
                            Audio (MP3)
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Highest quality available
                          </div>
                        </button>
                      </div>
                    </div>
                  )}

                  {isDownloading && (
                    <div className="glass-panel p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-foreground">
                          Downloading...
                        </span>
                        <span className="text-sm text-accent">
                          {downloadProgress.toFixed(0)}%
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-accent h-2 rounded-full smooth-transition"
                          style={{ width: `${downloadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {status === 'completed' && (
                    <div className="flex flex-col items-center space-y-4">
                      <div className="text-lg font-bold text-accent">
                        Download Complete!
                      </div>
                      <Button
                        onClick={handleClientDownload}
                        size="lg"
                        className="w-full glass-hover"
                        iconName="Download"
                        iconPosition="left"
                      >
                        Download File
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <hr />

            <div className="max-w-4xl mx-auto">
              <div className="glass-card p-8">
                <h2 className="text-fluid-2xl font-bold text-foreground mb-6">
                  Recent Downloads
                </h2>
                <div className="space-y-4">
                  {recentDownloads.length === 0 ? (
                    <div className="text-center text-muted-foreground">No recent downloads.</div>
                  ) : (
                    recentDownloads.map((download, index) => {
                      const downloadDate = new Date(download.date);
                      const now = new Date();
                      const timeDifference = now.getTime() - downloadDate.getTime();
                      const isExpired = timeDifference > 60 * 60 * 1000;

                      return (
                        <div key={index} className="glass-panel p-4 flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="glass-card p-3">
                              <Icon name={download.type === 'video' ? 'PlayCircle' : 'Music'} size={24} className="text-accent" />
                            </div>
                            <div>
                              <h3 className="font-medium text-foreground">
                                {download.title}
                              </h3>
                              <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                                <span>{download.type === 'video' ? 'Video' : 'Audio'}</span>
                                <span>•</span>
                                <span>{isExpired ? 'File Expired' : 'Available for ~' + Math.max(0, 60 - Math.floor(timeDifference / (1000 * 60))) + ' mins'}</span>
                              </div>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleReDownload(download.url, download.type)}
                            variant="ghost"
                            size="sm"
                            iconName="Download"
                            className="micro-lift"
                            disabled={isExpired}
                          >
                            {isExpired ? 'Expired' : 'Re-download'}
                          </Button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>

        <hr />

        <div className="relative" style={{ zIndex: 10 }}>
          <Footer />
        </div>
      </div>
    </>
  );
};

export default DownloadPage;